import { useState } from 'react';

import { Button, Group, List, Modal, Select, Stack, Text } from '@mantine/core';

import { useEscalationStatus } from '@/hooks/useEscalationStatus';
import { useSubjectEscalation } from '@/hooks/useSubjectEscalation';
import { SubjectScopeType } from '@/types';
import classes from './EscalateToGdmModal.module.css';

/** The report types this bridge can raise, in the order they are offered.
 *
 * A closed list rather than the templates endpoint: these are the scopes with
 * an automated pipeline behind them, and offering a template with no generators
 * would produce a project that activates and then never completes. Add an entry
 * here when a report type's components can actually be fulfilled. */
const GDM_SCOPES: { value: SubjectScopeType; label: string; description: string }[] = [
  {
    value: SubjectScopeType.GDM_3G,
    label: 'GDM-3G',
    description: 'Individual subject — sanctions, adverse issues, media research.',
  },
  {
    value: SubjectScopeType.GDM_1Y,
    label: 'GDM-1Y',
    description:
      'Individual subject — adds personal information, business interests and affiliations screening.',
  },
  {
    value: SubjectScopeType.GDM_1X,
    label: 'GDM-1X',
    description:
      'Company plus up to two key principals — corporate information, legal issues and affiliations. Bankruptcy checks are completed by a Network Partner, so the report is not delivered without them.',
  },
];

interface EscalateToGdmModalProps {
  opened: boolean;
  onClose: () => void;
  /** The report being escalated. Threaded to the backend so the automated
   * flow uses this exact report's screening thread (not the subject's latest). */
  reportId: string;
  /** The report's subjects. All are escalated under the chosen scope. */
  subjects: { id: string; subjectName: string }[];
  /** Scope selected when the modal opens. */
  defaultScope?: SubjectScopeType;
}

/**
 * Admin-only bridge: escalate a completed screening report's subject(s) to an
 * automated GDM report (with downstream Google Docs export). Re-exposes the
 * pre-existing subject-escalation/automated-project workflow from the report
 * page after a release hid its UI entry point.
 *
 * Gate the trigger on ``isAdministrator`` at the call site; the backend
 * ``/escalation/subjects`` endpoint is independently admin-gated.
 */
export function EscalateToGdmModal({
  opened,
  onClose,
  reportId,
  subjects,
  defaultScope = SubjectScopeType.GDM_3G,
}: Readonly<EscalateToGdmModalProps>) {
  const [isEscalating, setIsEscalating] = useState(false);
  const [scope, setScope] = useState<SubjectScopeType>(defaultScope);
  const { escalateSubjects } = useSubjectEscalation();
  const { startEscalations } = useEscalationStatus();

  const hasSubjects = subjects.length > 0;
  const selected = GDM_SCOPES.find((s) => s.value === scope);

  const handleConfirm = async () => {
    setIsEscalating(true);
    try {
      const result = await escalateSubjects(
        subjects.map((s) => ({
          id: s.id,
          name: s.subjectName,
          scope,
          report_id: reportId,
        }))
      );
      // Reuse the shared escalation poller for status + completion toasts.
      startEscalations(result);
      onClose();
    } catch {
      // escalateSubjects already surfaces a notification on failure; keep the
      // modal open so the admin can retry.
    } finally {
      setIsEscalating(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Escalate to a GDM report" centered>
      <Stack gap="md">
        <Select
          label="Report type"
          data={GDM_SCOPES.map(({ value, label }) => ({ value, label }))}
          value={scope}
          onChange={(value) => value && setScope(value as SubjectScopeType)}
          description={selected?.description}
          disabled={isEscalating}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
        />
        <Text size="sm">
          This will escalate the following subject{subjects.length > 1 ? 's' : ''} to a {scope}{' '}
          report with Google Docs export. This runs the automated GDM workflow and cannot be undone.
        </Text>
        <List size="sm" className={classes.subjectList}>
          {subjects.map((s) => (
            <List.Item key={s.id}>{s.subjectName}</List.Item>
          ))}
        </List>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" size="sm" onClick={onClose} disabled={isEscalating}>
            Cancel
          </Button>
          <Button
            color="grape"
            size="sm"
            onClick={handleConfirm}
            loading={isEscalating}
            disabled={!hasSubjects}
          >
            Escalate
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
